import { useEffect, useState } from 'react';
import type { ClinicalSeverityData } from '@/lib/clinicalSeverityTypes';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Search,
  Mic,
  Calendar,
  MapPin,
  User,
  Clock,
  ChevronRight,
  Paperclip,
  AlertTriangle,
  Users,
  Plus,
  ListChecks,
  Sparkles,
} from 'lucide-react';
import { ScheduledCase, Laterality } from '@/lib/templates/types';
import { format } from 'date-fns';
import { PatientConfirmDialog } from './PatientConfirmDialog';
import { StaffUploadDialog } from './StaffUploadDialog';
import { SoloSurgeonDropzone } from './SoloSurgeonDropzone';
import { StaffPreChartForm } from './StaffPreChartForm';
import { SoloDailyQueueList } from './SoloDailyQueueList';
import { useSoloDailyQueue, SoloQueuePatient } from '@/hooks/useSoloDailyQueue';
import type { SurgeonTemplate } from '@/lib/templates/types';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface PatientSelectionStepProps {
  searchResults: ScheduledCase[];
  selectedCase: ScheduledCase | null;
  isSearching: boolean;
  onSearch: (query: string) => void;
  onSelect: (c: ScheduledCase, autoMatchedTemplate?: SurgeonTemplate, extractedLaterality?: Laterality | null, clinicalContext?: string, clinicalSeverityData?: ClinicalSeverityData | null) => void;
}

/** Derive a mock auth status from case data for display */
function getAuthStatus(sc: ScheduledCase): { label: string; color: string } {
  if (!sc.payer_name || sc.payer_name === 'Self-Pay')
    return { label: 'No Auth Needed', color: 'bg-muted text-muted-foreground' };
  if (sc.status === 'completed')
    return { label: 'Ready to Submit', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
  if (!sc.patient_dob || !sc.patient_mrn)
    return { label: 'Missing Face Sheet', color: 'bg-amber-50 text-amber-700 border-amber-200' };
  return { label: 'Drafting', color: 'bg-sky-50 text-sky-700 border-sky-200' };
}

/** Convert a SoloQueuePatient to a ScheduledCase for the template engine */
function queuePatientToScheduledCase(p: SoloQueuePatient): ScheduledCase {
  return {
    id: p.id,
    patient_name: p.patient_name,
    patient_dob: p.patient_dob,
    patient_mrn: null,
    patient_sex: p.patient_sex,
    surgeon_id: '',
    facility_id: null,
    facility_name: p.facility_name,
    scheduled_date: p.appointment_time,
    scheduled_procedure: p.planned_procedure || null,
    encounter_id: null,
    payer_name: p.payer_name || 'Solo Surgeon Mode',
    plan_name: null,
    member_id: null,
    status: 'scheduled',
    is_demo: false,
    pre_loaded_docs: p.pre_loaded_docs,
  };
}

function buildPreLoadedClinicalContext(docs: SoloQueuePatient['pre_loaded_docs']): string {
  const documentNames = docs.map((doc) => doc.type).join(', ');
  const extractedText = docs
    .map((doc) => [doc.type, doc.extracted_text].filter(Boolean).join(':\n'))
    .filter(Boolean)
    .join('\n\n');

  return extractedText
    ? `Pre-loaded documents: ${documentNames}.\n\n${extractedText}`
    : `Pre-loaded documents: ${documentNames}.`;
}

export function PatientSelectionStep({
  searchResults,
  selectedCase,
  isSearching,
  onSearch,
  onSelect,
}: PatientSelectionStepProps) {
  const [query, setQuery] = useState('');
  const [pendingPatient, setPendingPatient] = useState<ScheduledCase | null>(null);
  const [staffMode, setStaffMode] = useState(false);
  const [staffUploadPatient, setStaffUploadPatient] = useState<ScheduledCase | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  // Local state to track staff-uploaded docs per patient id (for EHR queue)
  const [staffDocs, setStaffDocs] = useState<Record<string, { type: string; status: 'verified' | 'pending' }[]>>({});

  // Solo Daily Queue (persistent)
  const { sortedPatients, addPatient, addDocsToPatient, removePatient, clearQueue, patientCount } = useSoloDailyQueue();

  useEffect(() => {
    onSearch('');
  }, []);

  const handleSearch = (val: string) => {
    setQuery(val);
    onSearch(val);
  };

  const handlePatientClick = (sc: ScheduledCase) => {
    if (staffMode) {
      setStaffUploadPatient(sc);
    } else {
      setPendingPatient(sc);
    }
  };

  const handleSoloQueuePatientClick = (p: SoloQueuePatient) => {
    const sc = queuePatientToScheduledCase(p);
    if (staffMode) {
      // In staff mode, allow adding more docs
      setStaffUploadPatient(sc);
    } else {
      // Surgeon flow
      const hasDocs = p.pre_loaded_docs.length > 0;
      if (hasDocs) {
        // BYPASS: Skip confirm modal, go straight to template engine
        const clinicalContext = buildPreLoadedClinicalContext(p.pre_loaded_docs);
        onSelect(sc, undefined, undefined, clinicalContext);
        toast.success('Pre-loaded patient selected', {
          description: `${p.patient_name} — ${p.pre_loaded_docs.length} docs ready. Proceeding to template engine.`,
        });
      } else {
        // No docs — show normal confirm dialog
        setPendingPatient(sc);
      }
    }
  };

  const handleConfirm = (sc: ScheduledCase, clinicalContext?: string) => {
    setPendingPatient(null);
    onSelect(sc, undefined, undefined, clinicalContext);
  };

  const handleCancel = () => {
    setPendingPatient(null);
  };

  const handleStaffDocsUploaded = (patientId: string, docs: { type: string; status: 'verified' | 'pending' }[]) => {
    // Check if this is a solo queue patient
    const isSoloPatient = sortedPatients.some(p => p.id === patientId);
    if (isSoloPatient) {
      addDocsToPatient(patientId, docs.map(d => ({
        ...d,
        status: d.status as 'verified' | 'pending' | 'rejected',
        uploaded_by: 'Staff - Pre-Chart',
        uploaded_at: new Date().toISOString(),
      })));
    } else {
      setStaffDocs(prev => ({
        ...prev,
        [patientId]: [...(prev[patientId] || []), ...docs],
      }));
    }
    setStaffUploadPatient(null);
  };

  // Merge staff-uploaded docs into search results
  const enrichedResults = searchResults.map(sc => {
    const extra = staffDocs[sc.id];
    if (extra && extra.length > 0) {
      return {
        ...sc,
        pre_loaded_docs: [
          ...(sc.pre_loaded_docs || []),
          ...extra.map(d => ({ ...d, status: d.status as 'verified' | 'pending' | 'rejected' })),
        ],
      };
    }
    return sc;
  });

  return (
    <div className="space-y-5">
      {/* ── SECTION 1: Active Patient Queue (EHR) ── */}
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1.5">
          <h2 className="text-lg font-semibold text-foreground">Active Patient Queue</h2>
          <p className="text-sm text-muted-foreground">
            Patients with scheduled surgery encounters requiring prior authorization.
          </p>
        </div>
        {/* Staff View Toggle */}
        <Button
          variant={staffMode ? 'default' : 'ghost'}
          size="sm"
          className={cn(
            'text-[11px] shrink-0',
            staffMode ? '' : 'text-muted-foreground hover:text-foreground'
          )}
          onClick={() => setStaffMode(!staffMode)}
        >
          <Users className="w-3.5 h-3.5 mr-1" />
          {staffMode ? 'Exit Staff View' : 'Staff View (Simulate)'}
        </Button>
      </div>

      {staffMode && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-800 flex items-center gap-2">
          <Users className="w-3.5 h-3.5 shrink-0" />
          <span><strong>Staff Mode Active</strong> — Click any patient to upload clinical documents for pre-charting. This simulates a medical assistant's workflow.</span>
        </div>
      )}

      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder='Search by patient name or say "Patient [name]"...'
          value={query}
          onChange={e => handleSearch(e.target.value)}
          className="pl-10 pr-10 bg-card"
        />
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-1 top-1/2 -translate-y-1/2 w-8 h-8"
          title="Voice search"
        >
          <Mic className="w-4 h-4" />
        </Button>
      </div>

      {/* Patient Cards List (EHR) */}
      <div className="space-y-2.5 max-h-[35vh] overflow-y-auto pr-1">
        {isSearching ? (
          <div className="text-center py-8 text-muted-foreground text-sm">Searching scheduled cases…</div>
        ) : enrichedResults.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            No scheduled surgery encounters found.
          </div>
        ) : (
          enrichedResults.map(sc => {
            const status = getAuthStatus(sc);
            const isSelected = selectedCase?.id === sc.id;
            const preLoadedDocs = sc.pre_loaded_docs || [];
            const hasPreLoadedDocs = preLoadedDocs.length > 0;
            const verifiedCount = preLoadedDocs.filter(d => d.status === 'verified').length;

            return (
              <Card
                key={sc.id}
                className={cn(
                  'group cursor-pointer transition-all duration-200 border-l-[3px] hover:shadow-md',
                  isSelected
                    ? 'border-l-primary bg-primary/[0.03] shadow-sm'
                    : 'border-l-transparent hover:border-l-primary/50',
                  staffMode && 'ring-1 ring-amber-200/50'
                )}
                onClick={() => handlePatientClick(sc)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <User className="w-4 h-4 text-primary" />
                        </div>
                        <span className="font-semibold text-foreground">{sc.patient_name}</span>
                        <span className="text-xs text-muted-foreground">DOB: {sc.patient_dob || '—'}</span>
                        <span className="text-xs text-muted-foreground">MRN: {sc.patient_mrn ? `...${sc.patient_mrn.slice(-4)}` : '—'}</span>
                        {sc.patient_sex && <span className="text-xs text-muted-foreground capitalize">Sex: {sc.patient_sex}</span>}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {sc.scheduled_date ? format(new Date(sc.scheduled_date), 'MMM d, yyyy h:mm a') : 'Not scheduled'}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {sc.facility_name || '—'}
                        </span>
                      </div>
                      {sc.scheduled_procedure && (
                        <div className="flex items-center gap-1.5 text-xs">
                          <Clock className="w-3 h-3 text-muted-foreground shrink-0" />
                          <span className="text-foreground font-medium truncate">{sc.scheduled_procedure}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <Badge variant="outline" className="text-[10px] font-medium bg-muted/50 text-muted-foreground border-border rounded-full">
                        {sc.payer_name || 'No payer'}
                      </Badge>
                      <Badge variant="outline" className={cn('text-[10px] font-semibold rounded-full border', status.color)}>
                        {status.label}
                      </Badge>
                      {hasPreLoadedDocs ? (
                        <Badge variant="outline" className="text-[10px] font-semibold rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700">
                          <Paperclip className="w-3 h-3 mr-1" />{verifiedCount} Docs Pre-Loaded
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] font-medium rounded-full border border-border bg-muted/30 text-muted-foreground">
                          <AlertTriangle className="w-3 h-3 mr-1" />Missing Docs
                        </Badge>
                      )}
                      {sc.encounter_id && <span className="text-[10px] text-muted-foreground">CSN: {sc.encounter_id}</span>}
                      <Button variant="ghost" size="icon" className="w-7 h-7 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => { e.stopPropagation(); handlePatientClick(sc); }}>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* ── SECTION 2: Divider ── */}
      <div className="flex items-center gap-3 pt-2">
        <Separator className="flex-1" />
        <span className="text-xs text-muted-foreground whitespace-nowrap">Solo Surgeon Mode — Daily Queue</span>
        <Separator className="flex-1" />
      </div>

      {/* ── SECTION 3: Solo Daily Queue ── */}
      <div className="space-y-3">
        {/* Solo queue header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-sky-50 border border-sky-200">
              <Sparkles className="w-4 h-4 text-sky-600" />
              <span className="text-sm font-semibold text-sky-900">Today's Solo Queue</span>
              {patientCount > 0 && (
                <Badge variant="outline" className="text-[10px] border-sky-300 text-sky-700 bg-white/60">
                  {patientCount} patient{patientCount !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {patientCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-[11px] text-muted-foreground hover:text-destructive"
                onClick={() => {
                  clearQueue();
                  toast.info('Solo queue cleared');
                }}
              >
                Clear Queue
              </Button>
            )}
            <Button
              variant={showAddForm ? 'secondary' : 'outline'}
              size="sm"
              className="text-[11px]"
              onClick={() => setShowAddForm(!showAddForm)}
            >
              {showAddForm ? (
                'Cancel'
              ) : (
                <>
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Staff Pre-Charting
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Staff Add Patient Form */}
        {showAddForm && (
          <StaffPreChartForm
            onSave={(patient) => {
              addPatient(patient);
              setShowAddForm(false);
            }}
            onCancel={() => setShowAddForm(false)}
          />
        )}

        {/* Solo Queue Patient List */}
        <SoloDailyQueueList
          patients={sortedPatients}
          onPatientClick={handleSoloQueuePatientClick}
          onRemovePatient={staffMode ? removePatient : undefined}
          isStaffMode={staffMode}
        />

        {/* One-off Solo Surgeon Dropzone (for ad-hoc cases) */}
        <div className="flex items-center gap-3 pt-1">
          <Separator className="flex-1" />
          <span className="text-[11px] text-muted-foreground whitespace-nowrap">or upload a document directly</span>
          <Separator className="flex-1" />
        </div>

        <SoloSurgeonDropzone onPatientExtracted={(patient, template, laterality, clinicalContext) => onSelect(patient, template, laterality, clinicalContext)} />
      </div>

      {/* Confirm Dialog (surgeon flow) */}
      <PatientConfirmDialog
        patient={pendingPatient}
        open={!!pendingPatient}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      {/* Staff Upload Dialog */}
      <StaffUploadDialog
        patient={staffUploadPatient}
        open={!!staffUploadPatient}
        onDocsUploaded={handleStaffDocsUploaded}
        onCancel={() => setStaffUploadPatient(null)}
      />
    </div>
  );
}
